import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, Home, ArrowRight, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { apiRequest, formatPrice } from '../lib/utils';
import confetti from 'canvas-confetti';

export default function OrderSuccessPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const orderId = searchParams.get('order_id');
    const sessionId = searchParams.get('session_id');

    useEffect(() => {
        // Trigger confetti animation
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
        });

        // Load order details
        loadOrder();
    }, []);

    const loadOrder = async () => {
        try {
            if (sessionId) {
                // Verify payment status with Stripe
                await apiRequest(`/checkout/status/${sessionId}`);
            }
            
            if (orderId) {
                const orderData = await apiRequest(`/orders/${orderId}`);
                setOrder(orderData);
            }
        } catch (error) {
            console.error('Error loading order:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 flex items-center justify-center py-8 px-4">
            <Card className="glass rounded-3xl max-w-md w-full overflow-hidden">
                {/* Success Header */}
                <div className="bg-gradient-to-r from-emerald-500 to-green-600 p-8 text-center text-white">
                    <div className="w-20 h-20 rounded-full bg-white/20 mx-auto flex items-center justify-center mb-4 animate-bounce">
                        <CheckCircle className="h-12 w-12" />
                    </div>
                    <h1 className="text-2xl font-bold mb-2">Commande confirmée !</h1>
                    <p className="text-white/80">Merci pour votre achat</p>
                </div>

                <CardContent className="p-6 space-y-6">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : (
                        <>
                            {/* Order Reference */}
                            {orderId && (
                                <div className="text-center p-4 rounded-xl bg-muted/50">
                                    <p className="text-sm text-muted-foreground mb-1">Référence commande</p>
                                    <p className="font-mono font-bold text-lg text-primary">{orderId}</p>
                                </div>
                            )}

                            {/* Order Summary */}
                            {order && (
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center py-2 border-b">
                                        <span className="text-muted-foreground">Articles</span>
                                        <span>{order.items?.length || 0} produit(s)</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b">
                                        <span className="text-muted-foreground">Total payé</span>
                                        <span className="font-bold text-lg">{formatPrice(order.total)}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2">
                                        <span className="text-muted-foreground">Mode de retrait</span>
                                        <span>{order.delivery_type === 'delivery' ? 'Livraison' : 'En magasin'}</span>
                                    </div>
                                </div>
                            )}

                            {/* Email Notification */}
                            <div className="flex items-center gap-3 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                                <Mail className="h-5 w-5 text-blue-500 flex-shrink-0" />
                                <p className="text-sm">
                                    Un email de confirmation a été envoyé avec les détails de votre commande.
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="space-y-3 pt-4">
                                <Button 
                                    onClick={() => navigate('/orders')}
                                    className="w-full rounded-full h-12"
                                    data-testid="track-order-btn"
                                >
                                    <Package className="h-5 w-5 mr-2" />
                                    Suivre ma commande
                                    <ArrowRight className="h-5 w-5 ml-2" />
                                </Button>
                                
                                <Button 
                                    variant="outline"
                                    onClick={() => navigate('/')}
                                    className="w-full rounded-full"
                                >
                                    <Home className="h-5 w-5 mr-2" />
                                    Retour à l&apos;accueil
                                </Button>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
